import { DestroyRef, Injectable } from '@angular/core';
import { SbLineChartModel } from '@ngx-seatback/charts';
import {
  API,
  BookingDTO,
  CampaignDTO,
  CampaignWithPreviewDTO,
  CommercialDTO,
  CommercialWithPreviewDTO,
  FrameBucketDTO,
  LocalizedFrameBucketDTO,
  RequirementDTO,
} from './api/api';
import { SbLocalDateTime } from './api/api-date-time';
import { DateTime } from 'luxon';
import { take } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Injectable()
export class CampaignReportingService {
  from: SbLocalDateTime;
  till: SbLocalDateTime;

  constructor(
    private api: API,
    private destroyRef: DestroyRef
  ) {
    this.from = new SbLocalDateTime(DateTime.utc().startOf('day').minus({ day: 8 }));
    this.till = new SbLocalDateTime(DateTime.utc().endOf('day').minus({ day: 1 }));
  }

  private filterData(data: FrameBucketDTO[] | LocalizedFrameBucketDTO[], searchVal: number) {
    return data[data.findIndex(x => x.key.id === searchVal)].frames;
  }

  public getAndMergePulseChart(campaigns: any[]) {
    const campaignIds = [];
    if (campaigns?.length > 0) {
      for (const c of campaigns) {
        campaignIds.push(c.id);
      }
      if (campaigns[0] instanceof CampaignWithPreviewDTO || campaigns[0] instanceof CampaignDTO) {
        this.retrieveCampaignReporting(campaignIds, campaigns);
      }
      if (campaigns[0] instanceof CommercialWithPreviewDTO || campaigns[0] instanceof CommercialDTO) {
        this.retrieveCommercialReporting(campaignIds, campaigns);
      }
      if (campaigns[0] instanceof RequirementDTO) {
        this.retrieveRequirementsReporting(campaignIds, campaigns);
      }
      if (campaigns[0] instanceof BookingDTO) {
        this.retrieveBookingsReporting(campaignIds, campaigns);
      }

      return campaigns;
    }
  }

  private buildPulseChart(data: FrameBucketDTO[] | LocalizedFrameBucketDTO[], campaigns: any[]) {
    if (data && campaigns) {
      for (const a of campaigns) {
        const playerTimeChartData = [];
        let chartData: SbLineChartModel;
        const tempData = this.filterData(data, a.id);
        for (const i of tempData) {
          playerTimeChartData.push({
            day: i.range.from.value.toFormat('dd.MM'),
            playouts: i.data.productivePlayouts,
          });
        }
        chartData = {
          title: 'PlayoutPulse',
          height: 50,
          width: 100,
          valueAssignments: [
            {
              value: 'playouts',
              categories: ['day'],
              color: '#3E7CB1',
              valueName: 'Playouts',
            },
          ],
          data: playerTimeChartData,
          loadingDone: true,
        };

        campaigns[campaigns.findIndex(x => x.id === a.id)].reporting = chartData;
      }
    }
  }

  private retrieveCampaignReporting(campaignIds: any[], campaigns: any[]) {
    this.api
      .ds_cm_Reporting_campaignsReport({
        id: campaignIds,
        from: this.from,
        till: this.till,
        interval: 86400,
      })
      .pipe(takeUntilDestroyed(this.destroyRef), take(1))
      .subscribe((data: any) => {
        data = data.buckets;
        this.buildPulseChart(data, campaigns);
      });
  }

  private retrieveCommercialReporting(campaignIds: any[], campaigns: any[]) {
    this.api
      .ds_cm_Reporting_commercialsReport({
        id: campaignIds,
        from: this.from,
        till: this.till,
        interval: 86400,
      })
      .pipe(takeUntilDestroyed(this.destroyRef), take(1))
      .subscribe((data: any) => {
        data = data.buckets;
        this.buildPulseChart(data, campaigns);
      });
  }

  private retrieveRequirementsReporting(campaignIds: any[], campaigns: any[]) {
    this.api
      .ds_cm_Reporting_requirementsReport({
        id: campaignIds,
        from: this.from,
        till: this.till,
        interval: 86400,
      })
      .pipe(takeUntilDestroyed(this.destroyRef), take(1))
      .subscribe((data: any) => {
        data = data.buckets;
        this.buildPulseChart(data, campaigns);
      });
  }

  private retrieveBookingsReporting(campaignIds: any[], campaigns: any[]) {
    const filteredCampaigns: any[] = [];
    const filteredCampaignIds: any[] = [];

    for (let i = 0; i < campaigns.length; i++) {
      if (campaigns[i].medium) {
        filteredCampaignIds.push(campaignIds[i]);
        filteredCampaigns.push(campaigns[i]);
      }
    }
    this.api
      .ds_cm_Reporting_bookingsReport({ id: filteredCampaignIds, interval: 86400 })
      .pipe(takeUntilDestroyed(this.destroyRef), take(1))
      .subscribe((data: any) => {
        data = data.buckets;
        this.buildPulseChart(data, filteredCampaigns);
      });
  }
}
